import { normIcao } from "./airportFrequencyLocal";

/**
 * US domestic ICAO (contiguous K…, Alaska PA…, Hawaii PH…). Used so we do not infer
 * AK/HI departure tax on legs whose destination is outside the US.
 */
export function isUsDomesticIcao(icao: string): boolean {
  const n = normIcao(icao);
  if (n.length !== 4) return false;
  if (n.startsWith("K")) return true;
  if (n.startsWith("PA") || n.startsWith("PH")) return true;
  return false;
}

/** True if this airport is in Alaska or Hawaii (FAA ICAO: PA… / PH…). */
export function isAlaskaOrHawaiiIcao(icao: string): boolean {
  const n = normIcao(icao);
  return n.length === 4 && (n.startsWith("PA") || n.startsWith("PH"));
}

/**
 * Leg departs Alaska or Hawaii and arrives at a US domestic airport — IRS No. 26
 * Alaska/Hawaii domestic departure applies when passengers are not overridden to zero.
 */
export function inferredAlaskaHawaiiDomesticDepartureLeg(
  fromIcao: string,
  toIcao: string
): boolean {
  return (
    isAlaskaOrHawaiiIcao(fromIcao) &&
    isUsDomesticIcao(toIcao) &&
    normIcao(fromIcao) !== normIcao(toIcao)
  );
}

/**
 * Form 720 / IRS instructions: **IRS No. 26** domestic segment ($/segment) and **IRS No. 27**’s
 * Alaska–Hawaii departure-style amount ($11.70-style) can **both** apply on one leg when the flight is
 * **from** Alaska or Hawaii **to** the contiguous 48 states only (<code>K…</code> ICAO), e.g. PANC→KSEA.
 * For **intra-Alaska, intra-Hawaii, or AK↔HI** (destination is <code>PA…</code> or <code>PH…</code>),
 * this returns false — only the AK/HI departure line is used for that headcount in this app (no stacked domestic).
 */
export function isAlaskaHawaiiDepartureToContiguous48(
  fromIcao: string,
  toIcao: string
): boolean {
  if (!inferredAlaskaHawaiiDomesticDepartureLeg(fromIcao, toIcao)) return false;
  const to = normIcao(toIcao);
  return to.length === 4 && to.startsWith("K");
}
